import { escapeForRegEx } from '@tiptap/react';
import type { SuggestionMatch, Trigger } from '@tiptap/suggestion';

// 定义全角和半角 @ 符号
const HALF_WIDTH_AT = '@';
const FULL_WIDTH_AT = '＠';

/**
 * 自定义建议匹配函数，支持全角和半角 @ 符号
 * 覆盖 tiptap 默认实现的 findSuggestionMatch 函数（https://github.com/ueberdosis/tiptap/blob/main/packages/suggestion/src/findSuggestionMatch.ts#L18）
 * 查找文本中的 @query 格式的内容用于提示建议
 */
export const customFindSuggestionMatch = (config: Trigger): SuggestionMatch => {
  // 默认只支持配置 char，这里支持配置多个 @ 符号（chars）
  // 如果 char 是 @，则同时支持全角和半角；其他字符直接用传入的 char
  const chars = config.char === HALF_WIDTH_AT ? [HALF_WIDTH_AT, FULL_WIDTH_AT] : [config.char];

  const { allowSpaces: allowSpacesOption, allowToIncludeChar, allowedPrefixes, startOfLine, $position } = config;

  const allowSpaces = allowSpacesOption && !allowToIncludeChar;

  const text = $position.nodeBefore?.isText && $position.nodeBefore.text;

  if (!text) {
    return null;
  }

  const textFrom = $position.pos - text.length;

  // 和默认实现不同的地方：用 for 循环尝试匹配每种 @ 字符
  for (const char of chars) {
    const escapedChar = escapeForRegEx(char);
    const suffix = new RegExp(`\\s${escapedChar}$`);
    const prefix = startOfLine ? '^' : '';
    const finalEscapedChar = allowToIncludeChar ? '' : escapedChar;

    const regexp = allowSpaces
      ? new RegExp(`${prefix}${escapedChar}.*?(?=\\s${finalEscapedChar}|$)`, 'gm')
      : new RegExp(`${prefix}(?:^)?${escapedChar}[^\\s${finalEscapedChar}]*`, 'gm');

    const match = Array.from(text.matchAll(regexp)).pop();

    if (!match || match.input === undefined || match.index === undefined) {
      continue;
    }

    // JavaScript doesn't have lookbehinds. This hacks a check that first character
    // is a space or the start of the line
    if (allowedPrefixes !== null) {
      const matchPrefix = match.input.slice(Math.max(0, match.index - 1), match.index);
      const matchPrefixIsAllowed = new RegExp(`^[${allowedPrefixes.join('')}\\0]?$`).test(matchPrefix);

      if (!matchPrefixIsAllowed) {
        continue;
      }
    }

    // The absolute position of the match in the document
    const from = textFrom + match.index;
    let to = from + match[0].length;

    // Edge case handling; if spaces are allowed and we're directly in between
    // two triggers
    if (allowSpaces && suffix.test(text.slice(to - 1, to + 1))) {
      match[0] += ' ';
      to += 1;
    }

    // If the $position is located within the matched substring, return that range
    if (from < $position.pos && to >= $position.pos) {
      return {
        range: {
          from,
          to,
        },
        query: match[0].slice(char.length),
        text: match[0],
      };
    }
  }

  return null;
};
